import React from "react";

export interface ConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when user clicks "Cancel" button */
  onCancel: () => void;
  /** Called when user clicks "Confirm" button */
  onConfirm: () => void;
  /** Title of the modal */
  title?: string;
  /** Description text */
  message?: string;
  /** Text for the cancel button */
  cancelText?: string;
  /** Text for the confirm button */
  confirmText?: string;
  /** Style variant for confirm button */
  confirmVariant?: "primary" | "danger" | "success";
  /** Style variant for cancel button */
  cancelVariant?: "primary" | "danger" | "success";
  /** Additional CSS classes */
  className?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onCancel,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to continue?",
  cancelText = "Cancel",
  confirmText = "Confirm",
  confirmVariant = "primary",
  cancelVariant = "danger",
  className = "",
}) => {
  if (!isOpen) return null;

  const confirmButtonClasses = {
    primary: "tw-bg-blue-600 hover:tw-bg-blue-700 focus:tw-ring-blue-500",
    danger: "tw-bg-red-600 hover:tw-bg-red-700 focus:tw-ring-red-500",
    success: "tw-bg-green-600 hover:tw-bg-green-700 focus:tw-ring-green-500",
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4">
      {/* Backdrop */}
      <div
        className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-backdrop-blur-sm tw-transition-opacity"
        onClick={onCancel}
      />

      {/* Modal */}
      <div
        className={`tw-relative tw-w-full tw-max-w-md tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-shadow-xl tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-overflow-hidden ${className}`}
      >
        {/* Header */}
        <div className="tw-p-6 tw-border-b tw-border-gray-200 dark:tw-border-[#364152]">
          <div className="tw-flex tw-items-start tw-gap-4">
            {/* Icon */}
            <div className="tw-flex-shrink-0 tw-w-10 tw-h-10 tw-bg-blue-100 dark:tw-bg-blue-900/20 tw-rounded-full tw-flex tw-items-center tw-justify-center">
              <span className="material-symbols-outlined tw-text-blue-600 dark:tw-text-blue-400 tw-text-[24px]">
                help
              </span>
            </div>

            <div className="tw-flex-1">
              <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white tw-mb-2">
                {title}
              </h3>
              <p className="tw-text-sm tw-text-gray-600 dark:tw-text-gray-300 tw-leading-relaxed">
                {message}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="tw-p-6 tw-bg-gray-50 dark:tw-bg-[#151f28]">
          <div className="tw-flex tw-flex-col-reverse sm:tw-flex-row tw-gap-3 sm:tw-justify-end">
            {/* Cancel Button */}
            <button
              onClick={onCancel}
              className={`
                tw-w-full sm:tw-w-auto tw-px-6 tw-py-2.5 tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-bg-white dark:tw-bg-[#1C252E] tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-rounded-lg hover:tw-bg-gray-50 dark:hover:tw-bg-[#233648] tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2
                ${
                  cancelVariant === "danger"
                    ? "tw-text-red-500 tw-border-red-500 hover:tw-bg-red-50 dark:hover:tw-bg-[#4b2626]"
                    : ""
                }`}
            >
              {cancelText}
            </button>

            {/* Confirm Button */}
            <button
              onClick={onConfirm}
              className={`tw-w-full sm:tw-w-auto tw-px-6 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-rounded-lg tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 ${confirmButtonClasses[confirmVariant]}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
